import React, { useMemo, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";

import {
  ActionsSection,
  ButtonsSection,
} from "../../components/data-components/sections/DataSections";
import {
  DeleteInspection,
  DeleteInspectionFromAssignedProject,
  ManageDeletedData,
  ViewInpsectionsFromAssignedProject,
  ViewInspectionsList,
} from "../../services/authorization/Permissions";
import { SectionMenuItemsModel } from "../sewer-objects/section-details/SectionMenuItems";
import PageLayout from "../_layout/PageLayout";
import DataGrid from "../../components/datatable/DataGrid";
import DeleteButton from "../../components/buttons/delete-button/DeleteButton";
import { CurrentSectionStorageModel } from "../../services/sewer-object/CurrentSectionStorage";
import useErrorHandler, {
  ApplicationError,
  errorHandler,
} from "../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../services/authorization/UserPermissionsService";
import { InspectionsStorageModel } from "../../services/inspection/InspectionsStorage";
import { api } from "../../services/api/ApiService";
import useUrlParams from "../../services/url/UrlParamsService";
import { snackbarNotificationsService } from "../../services/notifications/SnackbarNotificationsService";
import { useHistory } from "react-router-dom";
import useRouteService from "../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../services/breadcrumbs/BreadcrumbsStorage";
import { translationService } from "../../services/translation/TranslationService";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { isUserOnProjects } = useRouteService();
  const { handleErrorInModal } = useErrorHandler();

  const [currentSectionStorage] = useState(() =>
    CurrentSectionStorageModel.create(
      {
        currentSectionId: urlParamsService.getSectionId()!,
        parentEntityFromPath: {
          id: urlParamsService.getSewerObjectParentId()!,
          type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job,
        },
      },
      { api }
    )
  );

  const [inspectionsStorage] = useState(() =>
    InspectionsStorageModel.create(
      { sectionId: urlParamsService.getSectionId()!, useGridFiltering: true },
      { api }
    )
  );

  const [isDeleting, setIsDeleting] = useState(false);

  const menuItems = useMemo(() => {
    const sectionMenuItems = SectionMenuItemsModel.create({
      sectionId: currentSectionStorage.currentSectionId,
      section: currentSectionStorage.currentSection,
      parentIdFromPath: currentSectionStorage.parentEntityFromPath.id,
      shouldDisableMenuItems: isDeleting,
    });

    return {
      staticItems: sectionMenuItems.getStaticMenuItems(),
      dynamicItems: sectionMenuItems.getDynamicMenuItems(),
    };
  }, [
    currentSectionStorage.currentSectionId,
    currentSectionStorage.currentSection,
    currentSectionStorage.parentEntityFromPath.id,
    isDeleting,
  ]);

  const isDeleteDisabled = useCallback(
    () =>
      !permissionsService.userHasOneOfThePermissions([
        DeleteInspection,
        DeleteInspectionFromAssignedProject,
      ]) || !inspectionsStorage.anyItemsSelected,
    [inspectionsStorage.anyItemsSelected]
  );

  const deleteSelectedInspections = useCallback(async () => {
    setIsDeleting(true);
    const selectedInspectionsCount = inspectionsStorage.selectedItemsCount;

    try {
      await inspectionsStorage.validateIfInspectionsCanBeDeleted(inspectionsStorage.selectedItems);
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withModalTitleKey("validation.inspections.relevance.deleteError.title");
      handleErrorInModal(applicationError);
      setIsDeleting(false);
      return;
    }

    try {
      await inspectionsStorage.deleteInspections(inspectionsStorage.selectedItems);
      snackbarNotificationsService.showSuccess("InspectionsPage.delete.success", {
        itemsCount: selectedInspectionsCount,
      });
      await currentSectionStorage.load();
    } catch (error) {
      const errorContext = { itemsCount: selectedInspectionsCount };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.inspections.delete");
      errorHandler.handleErrorInSnackbar(applicationError);
    }

    setIsDeleting(false);
  }, [inspectionsStorage, handleErrorInModal, currentSectionStorage]);

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([
        {
          entity: currentSectionStorage.parentEntityFromPath.type,
          name: await currentSectionStorage.getParentEntityName(
            currentSectionStorage.currentSection.project.name
          ),
        },
        {
          entity: EntityTypeFromPath.SewerObject,
          name: currentSectionStorage.currentSection.name,
        },
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [currentSectionStorage, currentSectionStorage.currentSection, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewInspectionsList, ViewInpsectionsFromAssignedProject]}
      titleKey="InspectionsPage.title"
      titleContext={{ sectionName: currentSectionStorage.currentSection.name }}
      menuItems={menuItems}
    >
      <ActionsSection>
        <ButtonsSection>
          <DeleteButton
            disabled={isDeleteDisabled()}
            confirmationAction={deleteSelectedInspections}
            confirmationModalTitleKey="InspectionsPage.delete.confirmation.title"
            confirmationModalTitleContext={{
              inspectionsCount: inspectionsStorage.selectedItemsCount,
            }}
            confirmationModalSubtitleKey="InspectionsPage.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
        </ButtonsSection>
      </ActionsSection>
      <DataGrid
        itemTypeKey="general.inspections"
        rowClickPath="inspections/:id"
        dataGridStorage={inspectionsStorage}
        customLocalizationMappings={{
          filterValueTrue: translationService.translate("general.inspection.relevant"),
          filterValueFalse: translationService.translate("general.inspection.outdated"),
        }}
      />
    </PageLayout>
  );
});
